import OnspaceVideoPlayerControls from '@onpace/onspace-media/elements/player/video/controls'

import translation from '@onpace/onspace-core/components/translations'

const isTouchScreen = window.matchMedia('(pointer: coarse)').matches

/// The Onspace Multi Player controls is an element to control a Multi-Video Player.
export default class OnspaceMultiVideoPlayerControls extends OnspaceVideoPlayerControls {
  /// Runs when the controls element is first connected to the DOM.
  runFirstConnected(options = {}) {
    this.multiPlayer = options.multiPlayer

    super.runFirstConnected(options)

    this.multiPlayer.addEventListener('onspace:media:multi-video:items-changed', this.multiPlayerItemsChanged.bind(this))
    this.multiPlayer.addEventListener('onspace:media:multi-video:players-changed', this.multiPlayerPlayersChanged.bind(this))
    this.multiPlayer.addEventListener('onspace:media:multi-video:presentation-changed', this.multiPlayerPresentationChanged.bind(this))
    this.multiPlayer.addEventListener('onspace:media:multi-video:tabs-changed', this.multiPlayerTabsChanged.bind(this))

    this.setupSelectionTab()
  }

  ////////// Interaction

  ///// Pointer

  /// Responds to presses on the element.
  ///
  /// If the player wasn't initially able to resume playback with audio, this will do that. It otherwise behaves
  /// differently depending on the pointer type:
  /// - For mice, this will toggle playback.
  /// - For touch, this will toggle the visibility of the element.
  pressed(event) {
    if (this.tabsElement && this.tabsElement.expanded) { return super.pressed(event) }

    const selectedPlayer = this.multiPlayer.selectedPlayerForEvent(event)

    if (selectedPlayer && selectedPlayer != this.player) {
      this.multiPlayer.activatePlayer(selectedPlayer.multiScreenId)
    } else {
      super.pressed(event)
    }
  }

  ///// Keyboard

  /// Responds to keyboard presses beginning on the document.
  keyPressBegan(event) {
    switch (event.key) {
    case 'f':
      this.multiPlayer.toggleFullscreen()
      break
    case 'l':
      this.multiPlayer.togglePlayersLayout()
      break
    default:
      return super.keyPressBegan(event)
    }

    event.preventDefault()
    event.stopPropagation()
    return false
  }

  ////////// Multi Player

  /// Responds to the multi player's items changing.
  multiPlayerItemsChanged(_event) {
    this.updateSelectionTab()
  }

  /// Responds to the players in the multi player changing.
  multiPlayerPlayersChanged(_event) {
    this.updateMultiScreenControls()
  }

  /// Responds to the multi player's tabs changing.
  multiPlayerTabsChanged(_event) {
    this.updateTabsElement()
  }

  /// Responds to the presentation state of the multi player changing.
  multiPlayerPresentationChanged(_event) {
    this.updateActionControls()
    this.updateMultiScreenControls()
    this.updatePresentationControls()
    this.updateTabsElement()
  }

  ////////// Player

  /// Responds to the presentation state of the player changing.
  playerPresentationChanged(event) {
    super.playerPresentationChanged(event)

    this.updateMultiScreenControls()
  }

  ////////// Multi Screen

  /// Initialises the elements which control multi-screen.
  setupMultiScreenControls() {
    if (this.multiScreenLayoutItem) { return }

    this.multiScreenLayoutItem = this.addMoreMenuItem('multiscreen-layout', translation('onspace.media.player.multi_screen.layout.title'), { icon: 'onspace/player_multiscreen', pressed: this.multiScreenLayoutPressed.bind(this) })
  }

  /// Updates the state of multi-screen controls.
  updateMultiScreenControls() {
    if (!this.multiScreenLayoutItem) { return }

    if (this.multiPlayer.hasMultiplePlayers) {
      this.multiScreenLayoutItem.hidden = false
      this.multiScreenLayoutItem.setButtonIcon(`onspace/player_multiscreen_${this.multiPlayer.playersOrientation}_${this.multiPlayer.playersLayout}${this.multiPlayer.players.length}`)
    } else {
      this.multiScreenLayoutItem.hidden = true
    }
  }

  ///// Events

  /// Responds to presses on the multi player layout button.
  multiScreenLayoutPressed(_event) {
    this.showMenu(this.multiScreenLayoutItem, [
      {
        title: translation('onspace.media.player.multi_screen.layout.grid'),
        icon: `onspace/player_multiscreen_${this.multiPlayer.playersOrientation}_grid${this.multiPlayer.players.length}`,
        pressed: () => this.multiScreenLayoutSelected('grid')
      }, {
        title: translation('onspace.media.player.multi_screen.layout.asymmetric'),
        icon: `onspace/player_multiscreen_${this.multiPlayer.playersOrientation}_asymmetric${this.multiPlayer.players.length}`,
        pressed: () => this.multiScreenLayoutSelected('asymmetric')
      }
    ])
  }

  /// Responds to a selection in the multi screen layout menu.
  multiScreenLayoutSelected(value) {
    this.multiPlayer.playersLayout = value
  }

  //////////

  /// Updates the elements for action controls.
  updateActionControls() {
    super.updateActionControls()

    if (!this.closeButton) { return }

    if (this.multiPlayer.hasCloseAction) {
      this.closeButton.style.display = ''
    } else {
      this.closeButton.style.display = 'none'
    }
  }

  /// Responds to clicks on the close button.
  closePressed() {
    this.multiPlayer.performCloseAction()
  }

  ////////// Presentation

  /// Initialises the elements which control presentation.
  setupPresentationControls() {
    this.setupMultiScreenControls()

    super.setupPresentationControls()
  }

  /// Updates the state of presentation controls.
  updatePresentationControls() {
    if (!this.fullscreenButton) { return }

    super.updatePresentationControls()

    if (this.multiPlayer.canEnterFullscreen) {
      this.fullscreenButton.style.display = ''
      this.fullscreenButton.setIcon('onspace/player_fullscreen_enter')
    } else if (this.multiPlayer.fullscreenActive && !isTouchScreen) {
      this.fullscreenButton.style.display = ''
      this.fullscreenButton.setIcon('onspace/player_fullscreen_exit')
    } else {
      this.fullscreenButton.style.display = 'none'
    }
  }

  /// Responds to presses on the Full Screen button.
  fullscreenPressed(_event) {
    this.multiPlayer.toggleFullscreen()
  }

  ////////// Tabs

  /// Retrieves the tabs provided by the system.
  ///
  /// This overrides OnspacePlayerControls.systemTabData to include the multi screen player's selection controls.
  get systemTabData() {
    if (this.multiPlayer.canSelectMedia) {
      return [this.selectionTab, ...super.systemTabData]
    } else {
      return super.systemTabData
    }
  }

  /// Retrieves the tabs provided for the media being played.
  ///
  /// This overrides OnspacePlayerControls.playerTabData to include the tabs of the multi screen player, so that the
  /// individual players only need to know about their own tabs.
  get playerTabData() {
    return [...super.playerTabData, ...this.multiPlayer.tabData]
  }

  /// Determines if the multi player's selection tab is active.
  get selectionTabExpanded() {
    return this.multiPlayer.canSelectMedia && this.tabsElement.selectedTabIndex === 0
  }

  ///// Events

  /// Responds to the tabs element showing its content.
  tabsExpanded(event) {
    super.tabsExpanded(event)

    if (this.selectionTabExpanded) {
      this.multiPlayer.classList.add('onspace-multi-video--selecting')
    } else {
      this.multiPlayer.classList.remove('onspace-multi-video--selecting')
    }
  }

  /// Responds to the tabs element hiding its content.
  tabsCollapsed(event) {
    super.tabsCollapsed(event)

    this.multiPlayer.classList.remove('onspace-multi-video--selecting')
  }

  ////////// Selection

  /// Sets up the selection tab with initial data.
  setupSelectionTab() {
    this.selectionTab = {
      title: translation('onspace.media.player.multi_screen.selection_tab'),
      type: 'selector',
      mediaIds: []
    }

    this.updateSelectionTab()
  }

  /// Updates the data available on the selection tab.
  updateSelectionTab() {
    if (!this.selectionTab) { return }

    this.selectionTab.items = this.multiPlayer.items.map((item) => {
      let activeIndex = this.multiPlayer.players.findIndex((player) => player.multiScreenId === item.id)
      if (activeIndex < 0) {
        activeIndex = null
      } else {
        activeIndex = (activeIndex + 1).toString()
      }

      return {
        metadata: item,
        action: () => this.multiPlayer.togglePlayer(item.id),
        indicatorText: activeIndex
      }
    })

    if (this.selectionTabExpanded) {
      this.tabsElement.updateTabContent(this.selectionTab)
    }
  }
}

window.customElements.define('onspace-multi-video-controls', OnspaceMultiVideoPlayerControls)
